import { produce } from 'immer';
import { atom, selector } from 'recoil';

import { CarFilter, SpecCountType } from '@/types/filters/filters.types';
import { guardRecoilDefaultValue } from '@/utils/atom';

export const drivetrainTypesState = atom<CarFilter[]>({
  key: 'drivetrainTypes',
  default: [],
});

export const driveTrainCountState = atom<SpecCountType[]>({
  key: 'driveTrainCount',
  default: [],
});

export const drivetrainTypeSearchOptionsSelector = selector({
  key: 'drivetrainTypeSearchOptionsSelector',
  get: ({ get }) => {
    const drivetrainTypes = get(drivetrainTypesState);
    const drivetrainTypeSearchOptions: CarFilter[] = drivetrainTypes.filter(
      (option) => !option.isSelected,
    );

    return { drivetrainTypeSearchOptions };
  },
});

export const toggleDrivetrainTypeSelector = selector<number | null>({
  key: 'toggleDrivetrainType',
  get: () => null,
  set: ({ set, get }, drivetrainTypeId) => {
    const drivetrainTypes = get(drivetrainTypesState);

    const updatedDrivetrainType = produce(
      drivetrainTypes,
      (drivetrainTypesDraft) => {
        if (drivetrainTypeId) {
          const drivetrainType = drivetrainTypesDraft.find(
            (drivetrainType) => drivetrainType.id === drivetrainTypeId,
          );

          drivetrainType!.isSelected = !drivetrainType?.isSelected;
          return;
        }
      },
    );

    set(drivetrainTypesState, updatedDrivetrainType);
  },
});

export const selectedDrivetrainTypesSelector = selector<CarFilter[]>({
  key: 'selectedDrivetrainTypes',
  get: ({ get }) => {
    const drivetrainTypes = get(drivetrainTypesState);

    return drivetrainTypes.filter(
      (drivetrainType) => drivetrainType.isSelected,
    );
  },
});

export const hasSelectedDrivetrainFilterSelector = selector<boolean>({
  key: 'hasSelectedDrivetrainFilter',
  get({ get }) {
    return Boolean(get(selectedDrivetrainTypesSelector).length);
  },
});

export const resetSelectedDrivetrainTypesSelector = selector<number | null>({
  key: 'resetSelectedDriveTrainTypes',
  get: () => null,
  set: ({ set, get }, drivetrainTypeId) => {
    const drivetrainTypes = get(drivetrainTypesState);

    const updatedDrivetrainType = produce(
      drivetrainTypes,
      (drivetrainTypesDraft) => {
        if (drivetrainTypeId) {
          const drivetrainType = drivetrainTypesDraft.find(
            (drivetrainType) => drivetrainType.id === drivetrainTypeId,
          );

          if (!drivetrainType) return;
          drivetrainType.isSelected = false;
          return;
        }

        drivetrainTypesDraft.forEach((drivetrainType) => {
          if (drivetrainTypeId && drivetrainType.id === drivetrainTypeId) {
            drivetrainType.isSelected = false;
            return;
          }
          drivetrainType.isSelected = false;
        });
      },
    );

    set(drivetrainTypesState, updatedDrivetrainType);
  },
});

export const selectDrivetrainTypeSelector = selector({
  key: 'selectDrivetrainType',
  get: () => [],
  set: ({ set, get }, drivetrainIds) => {
    const drivetrainTypes = get(drivetrainTypesState);

    const updatedDrivetrainType = produce(
      drivetrainTypes,
      (drivetrainTypesDraft) => {
        if (guardRecoilDefaultValue(drivetrainIds)) return;
        if (!drivetrainIds) return;

        if (drivetrainIds.length) {
          drivetrainIds
            .map((drivetrainTypeId) => Number(drivetrainTypeId))
            .forEach((drivetrainTypeId) => {
              const drivetrain = drivetrainTypesDraft.find(
                (drivetrain) => drivetrain.id === drivetrainTypeId,
              );

              if (!drivetrain) return;
              drivetrain.isSelected = true;
            });

          return;
        }
      },
    );

    set(drivetrainTypesState, updatedDrivetrainType);
  },
});

export const visibleDrivetrainTypeSelector = selector({
  key: 'visibleDrivetrainTypeSelector',
  get: ({ get }) => {
    const drivetrainTypes = get(drivetrainTypesState);
    const driveTrainCount = get(driveTrainCountState);

    return drivetrainTypes.map((drivetrainType) => ({
      ...drivetrainType,
      isDisabled: !driveTrainCount?.find(
        (group) => group?.specId === drivetrainType?.id,
      )?.count,
    }));
  },
});
